import React, { useState, useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import PropTypes from "prop-types";
import { formatElements } from "./parseText";

// Components
import Text from "new_components/atoms/Text";
import IconWithBackground from "new_components/molecules/IconWithBackground";

// Constants
import colors from "styles/colors";
import typography from "styles/typography";
import { ICON_SET } from "new_components/atoms/Icon/IconSet";
import Icon from "new_components/atoms/Icon";

// Keyframe animation for streaming text
const streamIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Keyframe for for wrapping component
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

// Update the MessageContainer to only animate on first render
const MessageContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  margin-bottom: 16px;
  animation: ${(props) => (!props.hasRendered ? fadeIn : "none")} 0.5s ease-in;
  position: relative;

  &:hover .copy-icon {
    opacity: 1;
  }
`;

const CopyIconWrapper = styled.div`
  bottom: 0;
  opacity: 0;
  left: 0;
  transition: opacity 0.2s ease-in-out;
  cursor: pointer;
  padding: 8px;
`;

const MessageBubble = styled.div`
  max-width: 100%;
  word-wrap: break-word;
`;

const TypingIndicator = styled.div`
  display: flex;
  align-items: center;
  margin-top: 4px;
  font-size: 14px;
  font-family: "Raleway", sans-serif;
  color: #888;

  span {
    animation: ${streamIn} 1s infinite alternate;
  }
`;

const IconPosition = styled.div`
  margin-right: 20px;
`;

const ChatResponse = ({ message, isStreaming, isThinking, scrollRef }) => {
  // Add a ref to track if this message has already been rendered
  const hasRenderedRef = useRef(false);
  const [textCopied, setTextCopied] = useState(false);

  useEffect(() => {
    // Mark as rendered once we get content
    if (message && message.length > 0) {
      hasRenderedRef.current = true;
    }
  }, [message]);
  /**
   * We track:
   * 1) currentFullText: the text we're currently rendering
   * 2) displayedLength: how many characters we've revealed in the UI
   */
  const [currentFullText, setCurrentFullText] = useState("");
  const [displayedLength, setDisplayedLength] = useState(0);

  /**
   * If we're no longer streaming, show the entire text right away
   * If we are streaming, increment from displayedLength to new message length
   */
  useEffect(() => {
    // If message is an empty object, return
    if (!message) return;

    if (!isStreaming && message) {
      // "Complete" or no streaming → just show the full message
      setCurrentFullText(message);
      setDisplayedLength(message?.length);
      return;
    }

    /**
     * If we *are* streaming, check if the new message is longer than the old one.
     * - If the message is brand new or got shorter (rare, but can happen), reset from 0
     * - Otherwise, only animate the newly added characters
     */
    if (message?.length < currentFullText.length) {
      // The server replaced the message with a shorter text (rare),
      // so we reset from 0
      setCurrentFullText(message);
      setDisplayedLength(0);
    } else {
      // We have the old text or it's the same text, now possibly appended
      setCurrentFullText(message);
    }
  }, [message, isStreaming]);

  /**
   * A separate effect handles animating from displayedLength → message.length
   */
  useEffect(() => {
    if (!isStreaming) return;

    // If we've already displayed everything or there's nothing to display, stop
    if (displayedLength >= currentFullText.length) return;

    // We'll reveal one character every few ms
    const intervalMs = 15; // adjust speed as desired
    const intervalId = setInterval(() => {
      setDisplayedLength((prev) => {
        const nextVal = prev + 1;
        if (nextVal >= currentFullText.length) {
          clearInterval(intervalId);
        }
        return nextVal;
      });
    }, intervalMs);

    return () => clearInterval(intervalId);
  }, [isStreaming, currentFullText, displayedLength]);

  /**
   * The displayed text is the substring up to displayedLength
   */
  const formattedText = formatElements(currentFullText);


  const handleCopyText = () => {
    navigator.clipboard.writeText(message);
    setTextCopied(true);
    setTimeout(() => setTextCopied(false), 2000); // Reset after 2 seconds
  };

  return (
    <MessageContainer hasRendered={hasRenderedRef.current}>
      <IconPosition>
        <IconWithBackground
          name={ICON_SET.aiIcon}
          color={colors.background.white}
          stroke={colors.background.white}
          bgColor={colors.primary[500]}
        />
      </IconPosition>
      {isThinking && (
        <TypingIndicator>
          <span>Thinking...</span>
        </TypingIndicator>
      )}
      {!isThinking && (
        <MessageBubble>
          <Text typographyStyle={typography.body.regular}>{formattedText}</Text>
          <CopyIconWrapper className="copy-icon" onClick={handleCopyText}>
            <Icon
              name={textCopied ? ICON_SET.checkMark : ICON_SET.copy}
              size={15}
              color={colors.neutral[300]}
              fill={colors.neutral[300]}
              style={{
                cursor: "pointer",
              }}
            />
          </CopyIconWrapper>
        </MessageBubble>
      )}
    </MessageContainer>
  );
};

ChatResponse.propTypes = {
  message: PropTypes.string.isRequired,
  isStreaming: PropTypes.bool,
  isThinking: PropTypes.bool,
  scrollRef: PropTypes.object,
};

ChatResponse.defaultProps = {
  isStreaming: false,
  isThinking: false,
};

export default ChatResponse;
